import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  article?: {
    publishDate: string;
    author: string;
  };
}

const BASE_URL = 'https://www.linkdiry.com';

export default function SEO({ title, description, canonical, ogImage, ogType = 'website', noindex, article }: SEOProps) {
  const fullTitle = title.includes('LinkDiry') ? title : `${title} | LinkDiry`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
  const imageUrl = ogImage || `${BASE_URL}/og-image.png`;

  const breadcrumbSchema = canonical ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      ...(canonical.startsWith('/blog/') && canonical !== '/blog' ? [
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${BASE_URL}/blog` },
        { "@type": "ListItem", "position": 3, "name": title.split(' | ')[0] }
      ] : canonical.startsWith('/vs/') ? [
        { "@type": "ListItem", "position": 2, "name": "Compare", "item": `${BASE_URL}/vs` },
        { "@type": "ListItem", "position": 3, "name": title.split(' | ')[0] }
      ] : canonical.startsWith('/niche/') ? [
        { "@type": "ListItem", "position": 2, "name": "Browse", "item": `${BASE_URL}/browse` },
        { "@type": "ListItem", "position": 3, "name": title.split(' | ')[0] }
      ] : [
        { "@type": "ListItem", "position": 2, "name": title.split(' | ')[0] }
      ])
    ]
  } : null;

  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title.split(' | ')[0],
    "author": { "@type": "Person", "name": article.author },
    "datePublished": article.publishDate,
    "dateModified": article.publishDate,
    "publisher": {
      "@type": "Organization",
      "name": "LinkDiry",
      "logo": { "@type": "ImageObject", "url": `${BASE_URL}/favicon.svg` }
    },
    "image": imageUrl,
    "mainEntityOfPage": canonicalUrl || BASE_URL,
    "description": description
  } : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={article ? 'article' : ogType} />
      <meta property="og:image" content={imageUrl} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
      {articleSchema && (
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      )}
    </Helmet>
  );
}
